import { useState, useEffect } from 'react';
import { 
  Building2, 
  Plus, 
  Pencil, 
  Trash2, 
  Lock,
  Eye,
  EyeOff,
  LogOut,
  Users,
  Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast, Toaster } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_STORAGE_KEY = 'cod4x_token';

const buildAuthHeaders = (superAdminKey) => {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  const headers = {};

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  if (superAdminKey) {
    headers['X-Super-Admin-Key'] = superAdminKey;
  }

  return headers;
};

export default function SuperAdmin() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [storedPassword, setStoredPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    cnpj: '',
    address: '',
    phone: '',
    email: ''
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Validate password via backend API call
      const response = await axios.get(`${API_URL}/api/super-admin/companies`, {
        headers: buildAuthHeaders(password)
      });
      // If we get here, password is valid
      setIsAuthenticated(true);
      setStoredPassword(password);
      sessionStorage.setItem('superAdminAuth', 'true');
      sessionStorage.setItem('superAdminKey', password);
      setCompanies(response.data);
      toast.success('Acesso autorizado!');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Senha incorreta!');
      } else {
        toast.error('Erro ao conectar ao servidor');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('superAdminAuth');
    sessionStorage.removeItem('superAdminKey');
    setPassword('');
    setStoredPassword('');
  };

  useEffect(() => {
    const auth = sessionStorage.getItem('superAdminAuth');
    const key = sessionStorage.getItem('superAdminKey');
    if (auth === 'true' && key) {
      setIsAuthenticated(true);
      setStoredPassword(key);
      fetchCompanies(key);
    }
  }, []);

  const fetchCompanies = async (key = storedPassword) => {
    if (!key) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/super-admin/companies`, {
        headers: buildAuthHeaders(key)
      });
      setCompanies(response.data);
    } catch (error) {
      toast.error('Erro ao carregar empresas');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCompany) {
        await axios.put(
          `${API_URL}/api/super-admin/companies/${editingCompany.id}`,
          formData,
          { headers: buildAuthHeaders(storedPassword) }
        );
        toast.success('Empresa atualizada com sucesso!');
      } else {
        const response = await axios.post(
          `${API_URL}/api/super-admin/companies`,
          formData,
          { headers: buildAuthHeaders(storedPassword) }
        );
        
        // Mostra informações do usuário Master criado
        const { master_user } = response.data;
        toast.success(
          <div>
            <strong>Empresa criada com sucesso!</strong>
            <br /><br />
            <strong>Usuário Master criado:</strong>
            <br />Email: {master_user.email}
            <br />Senha: {master_user.default_password}
          </div>,
          { duration: 10000 }
        );
      }
      setShowModal(false);
      resetForm();
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar empresa');
    }
  };

  const handleDelete = async (companyId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta empresa? Esta ação não pode ser desfeita.')) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/super-admin/companies/${companyId}`,
        { headers: buildAuthHeaders(storedPassword) }
      );
      toast.success('Empresa excluída');
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir empresa');
    }
  };

  const openEditModal = (company) => {
    setEditingCompany(company);
    setFormData({
      name: company.name,
      cnpj: company.cnpj || '',
      address: company.address || '',
      phone: company.phone || '',
      email: company.email || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingCompany(null);
    setFormData({
      name: '',
      cnpj: '',
      address: '',
      phone: '',
      email: ''
    });
  };

  // Tela de Login SuperMaster
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Toaster position="top-right" richColors />
        <div className="w-full max-w-md">
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-2xl border border-white/20">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-amber-500" />
              </div>
              <h1 className="text-2xl font-bold text-white">Painel SuperAdmin</h1>
              <p className="text-white/60 text-sm mt-2">Gestão de Empresas</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <Label className="text-white/80">Senha SuperMaster</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    placeholder="Digite a senha de acesso"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-amber-500 hover:bg-amber-600 text-black font-semibold"
              >
                Acessar Painel
              </Button>
            </form>

            <p className="text-center text-white/40 text-xs mt-6">
              Acesso restrito a administradores do sistema
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Painel de Gestão de Empresas
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="bg-black/20 backdrop-blur-lg border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">SuperAdmin Conferência</h1>
              <p className="text-xs text-white/60">Gestão de Empresas</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            onClick={handleLogout}
            className="text-white/60 hover:text-white hover:bg-white/10"
          >
            <LogOut size={18} className="mr-2" />
            Sair
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <p className="text-white/60 text-sm">Total de Empresas</p>
                <p className="text-2xl font-bold text-white">{companies.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-white/60 text-sm">Empresas Ativas</p>
                <p className="text-2xl font-bold text-white">{companies.filter(c => c.is_active !== false).length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center">
                <Shield className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <p className="text-white/60 text-sm">Nível de Acesso</p>
                <p className="text-2xl font-bold text-white">SuperMaster</p>
              </div>
            </div>
          </div>
        </div>

        {/* Companies List */}
        <div className="bg-white/5 backdrop-blur rounded-xl border border-white/10 overflow-hidden">
          <div className="p-6 border-b border-white/10 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-white">Empresas Cadastradas</h2>
              <p className="text-white/60 text-sm">Gerencie as empresas do sistema SaaS</p>
            </div>
            <Button 
              onClick={() => { resetForm(); setShowModal(true); }}
              className="bg-amber-500 hover:bg-amber-600 text-black"
            >
              <Plus size={18} className="mr-2" />
              Nova Empresa
            </Button>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mx-auto"></div>
              <p className="text-white/60 mt-4">Carregando...</p>
            </div>
          ) : companies.length === 0 ? (
            <div className="p-12 text-center">
              <Building2 className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <p className="text-white/60">Nenhuma empresa cadastrada</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left px-6 py-4 text-xs font-semibold text-white/60 uppercase">Empresa</th>
                    <th className="text-left px-6 py-4 text-xs font-semibold text-white/60 uppercase">CNPJ</th>
                    <th className="text-left px-6 py-4 text-xs font-semibold text-white/60 uppercase">Contato</th>
                    <th className="text-left px-6 py-4 text-xs font-semibold text-white/60 uppercase">Criado em</th>
                    <th className="text-center px-6 py-4 text-xs font-semibold text-white/60 uppercase">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map((company) => (
                    <tr key={company.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-blue-400" />
                          </div>
                          <div>
                            <p className="font-medium text-white">{company.name}</p>
                            <p className="text-xs text-white/60">{company.email || '-'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-white/80 font-mono text-sm">
                        {company.cnpj || '-'}
                      </td>
                      <td className="px-6 py-4 text-white/80 text-sm">
                        {company.phone || '-'}
                      </td>
                      <td className="px-6 py-4 text-white/60 text-sm">
                        {company.created_at ? new Date(company.created_at).toLocaleDateString('pt-BR') : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditModal(company)}
                            className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                          >
                            <Pencil size={16} />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(company.id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-slate-900 border-white/20 text-white">
          <DialogHeader>
            <DialogTitle>{editingCompany ? 'Editar Empresa' : 'Nova Empresa'}</DialogTitle>
            <DialogDescription className="text-white/60">
              {editingCompany ? 'Atualize os dados da empresa' : 'Preencha os dados para cadastrar uma nova empresa'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-white/80">Nome da Empresa *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="bg-white/10 border-white/20 text-white"
                placeholder="Nome da empresa"
                required
              />
            </div>

            <div>
              <Label className="text-white/80">CNPJ</Label>
              <Input
                value={formData.cnpj}
                onChange={(e) => setFormData({...formData, cnpj: e.target.value})}
                className="bg-white/10 border-white/20 text-white"
                placeholder="00.000.000/0000-00"
              />
            </div>

            <div>
              <Label className="text-white/80">Endereço</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                className="bg-white/10 border-white/20 text-white"
                placeholder="Endereço completo"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white/80">Telefone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="bg-white/10 border-white/20 text-white"
                  placeholder="(00) 00000-0000"
                />
              </div>
              <div>
                <Label className="text-white/80">Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="bg-white/10 border-white/20 text-white"
                  placeholder="contato@empresa.com"
                />
              </div>
            </div>

            <DialogFooter className="pt-4">
              <Button 
                type="button" 
                variant="ghost" 
                onClick={() => setShowModal(false)}
                className="text-white/60 hover:text-white"
              >
                Cancelar
              </Button>
              <Button type="submit" className="bg-amber-500 hover:bg-amber-600 text-black">
                {editingCompany ? 'Atualizar' : 'Criar Empresa'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
